import { Link } from 'react-router-dom';
import { ArrowUpRight, Mail, Phone, MapPin } from 'lucide-react';
import { usePublicSettings } from '@/hooks/useDynamicContent';

const DEFAULT_SETTINGS = {
  email: 'contact@scoreintegratedsolutions.com',
  phone: '(256) 427-9202',
  phone_tel: '+12564279202',
  business_address: '8401 Mayland Dr #4271, Richmond, VA 23294',
};

const footerLinks = [
  {
    title: 'Company',
    links: [
      { label: 'About Us', to: '/about' },
      { label: 'Services', to: '/services' },
      { label: 'Government', to: '/government' },
      { label: 'Careers', to: '/careers' },
      { label: 'Contact', to: '/contact' },
    ],
  },
  {
    title: 'Services',
    links: [
      { label: 'Technology Support', to: '/services' },
      { label: 'Systems Sustainment', to: '/services' },
      { label: 'Cybersecurity', to: '/services' },
      { label: 'Program Support', to: '/services' },
    ],
  },
  {
    title: 'More',
    links: [
      { label: 'Cloud & Infrastructure', to: '/services' },
      { label: 'Healthcare IT', to: '/services' },
      { label: 'Enterprise Services', to: '/services' },
      { label: 'User Support', to: '/services' },
    ],
  },
];

export default function Footer() {
  const settings = usePublicSettings(DEFAULT_SETTINGS);
  const phoneTel = settings.phone_tel || `+1${(settings.phone || '').replace(/[^0-9]/g, '')}`;
  return (
    <footer data-testid="footer" className="bg-[#0E0E0E] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6" style={{ width: '400px', overflow: 'visible', display: 'flex', alignItems: 'center' }}>
              <img 
                src="/logo-cropped.png"
                alt="Score Integrated Solutions LLC" 
                style={{ width: '360px', height: 'auto', maxHeight: '90px', objectFit: 'contain', objectPosition: 'left center', display: 'block' }}
              />
            </Link>
            <p className="text-[#A3A3A3] text-sm leading-relaxed max-w-sm mb-8">
              Technology support, systems sustainment, and operational services 
              for government and enterprise environments.
            </p>
            <div className="flex flex-col gap-3">
              <a href={`mailto:${settings.email}`} className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#F2B705] text-sm transition-colors whitespace-nowrap">
                <Mail size={14} className="shrink-0" />
                {settings.email}
              </a>
              <a href={`tel:${phoneTel}`} className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#F2B705] text-sm transition-colors">
                <Phone size={14} />
                {settings.phone}
              </a>
              <span className="flex items-center gap-2 text-[#A3A3A3] text-sm">
                <MapPin size={14} />
                {settings.business_address}
              </span>
            </div>
          </div>

          {/* Link columns */}
          {footerLinks.map((col) => (
            <div key={col.title}>
              <h4 className="text-xs uppercase tracking-widest text-[#F2B705] font-bold mb-6">
                {col.title}
              </h4>
              <ul className="flex flex-col gap-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm text-[#A3A3A3] hover:text-white transition-colors flex items-center gap-1 group"
                    >
                      {link.label}
                      <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-[#525252]">
            &copy; {new Date().getFullYear()} Score Integrated Solutions LLC. All rights reserved.
          </p>
          <div className="flex gap-6">
            <span className="text-xs text-[#525252] hover:text-[#A3A3A3] cursor-pointer transition-colors">Privacy Policy</span>
            <span className="text-xs text-[#525252] hover:text-[#A3A3A3] cursor-pointer transition-colors">Terms of Service</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
